import { Slider, SliderProps, useTheme } from "@mui/material";

import React from "react";

interface Mark {
  value: number;
  label: string;
}

interface Props extends SliderProps {
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number;
  marks?: Mark[];
  minLabel?: string;
  maxLabel?: string;
  whiteTrack?: boolean;
}

const BNSlider = React.forwardRef<HTMLElement, Props>(
  ({ min = 5000, max = 600000, defaultValue = 150000, step = 5000, whiteTrack, ...props }, ref) => {
    const theme = useTheme();

    const marks = props.marks ?? [
      {
        value: min,
        label: `${props.minLabel}`,
      },
      {
        value: max,
        label: `${props.maxLabel}`,
      },
    ];

    const valuetext = (value: number) => {
      return `${value} kr`;
    };

    const hasThreeMarks = marks.length === 3;

    return (
      <Slider
        aria-label="slider"
        defaultValue={defaultValue}
        getAriaValueText={valuetext}
        min={min}
        max={max}
        step={step}
        valueLabelDisplay="off"
        marks={marks}
        sx={[
          {
            height: "1.2rem",
            "& .MuiSlider-rail": whiteTrack
              ? {
                  backgroundColor: theme.vars.palette.shadow.white,
                  opacity: 1,
                }
              : { backgroundColor: theme.vars.palette.secondary.dark },
            "& .MuiSlider-track": {
              border: 0,
            },
            "& .MuiSlider-mark": {
              display: "none",
            },
            "& .MuiSlider-markLabel": {
              transform: "none",
            },
            "& span.MuiSlider-mark[data-index='1']": {
              display: hasThreeMarks ? "inherit" : "none",
              backgroundColor: theme.vars.palette.tertiary.main,
              width: "0.3rem",
              height: "0.3rem",
              outline: `2px ${theme.vars.palette.tertiary.main} solid`,
            },
            [`& .MuiSlider-markLabel[data-index='${marks.length - 1}']`]: {
              transform: "translateX(-100%)",
            },
            "& span.MuiSlider-markLabel:nth-of-type(2)": {
              transform: hasThreeMarks ? "none" : "translateX(-100%)",
            },
          },
          theme.applyStyles("dark", {
            "& .MuiSlider-rail": {
              backgroundColor: theme.vars.palette.secondary.lighter,
              border: `0.0625rem solid color-mix(in srgb, ${theme.vars.palette.shadow.black} 30%, transparent)`,
              opacity: 1,
            },
            "& .MuiSlider-track": {
              background: `linear-gradient(to right, ${theme.vars.palette.action.active}, rgb(120, 210, 255))`,
              border: 0,
              position: "relative",
              overflow: "hidden",
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                background: `linear-gradient(to bottom, color-mix(in srgb, ${theme.vars.palette.shadow.white} 30%, transparent) 0%, color-mix(in srgb, ${theme.vars.palette.shadow.white} 8%, transparent) 40%, transparent 60%, color-mix(in srgb, ${theme.vars.palette.shadow.black} 8%, transparent) 80%, color-mix(in srgb, ${theme.vars.palette.shadow.black} 5%, transparent) 100%)`,
                pointerEvents: "none",
                zIndex: 1,
              },
            },
            "& .MuiSlider-thumb": {
              background: `radial-gradient(circle at 35% 35%, #ffffff, #e8e8e8 50%, #c8c8c8 100%)`,
              boxShadow: `0 0.1rem 0.3rem rgba(0, 0, 0, 0.4), inset 0 0.1rem 0.1rem #ffffff, inset 0 -0.1rem 0.2rem #aaaaaa`,
            },
          }),
        ]}
        {...props}
        ref={ref}
      />
    );
  },
);
export default BNSlider;
